import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RadioGroup from '@components/fields/RadioGroup';
import Form from '@components/forms/Form';
import useGlobalOpener from '@hooks/useGlobalOpener';
import {
  ConsultCancelationReasonEnum,
  ConsultCancelDocument,
  ConsultCancelMutation,
  ConsultCancelMutationVariables
} from '@typing/Generated';
import { OpenerType } from '@typing/State';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  note: string | null;
  reason: ConsultCancelationReasonEnum;
};

type Props = {
  consultId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

const ConsultCancelForm = ({ consultId, onCancel, onSuccess }: Props) => {
  const initialValues = {
    note: null
  };

  const intl = useIntl();
  const timeTrackingOpener = useGlobalOpener(OpenerType.TIME_LOG);
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [cancelConsult] = useMutation<ConsultCancelMutation, ConsultCancelMutationVariables>(ConsultCancelDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      note: yup.string().nullable().defined(),
      reason: yup
        .string()
        .oneOf(Object.values(ConsultCancelationReasonEnum))
        .required(intl.formatMessage({ id: 'errors.consultCancel.reason.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return cancelConsult({
      variables: {
        ...form.getValues(),
        id: consultId
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.consultCancel,
        intl,
        onSuccess: () => {
          if (form.getValues().reason === ConsultCancelationReasonEnum.no_show) {
            timeTrackingOpener.open();
          }
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.consultCancel'
      });
    });
  }, [cancelConsult, form, consultId, intl, timeTrackingOpener, onSuccess]);

  const cancelReasonOptions = useMemo(() => {
    const options = [ConsultCancelationReasonEnum.no_show, ConsultCancelationReasonEnum.standard];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.consult.${option}` }),
      value: option
    }));
  }, [intl]);

  return (
    <Form data-test="care-profile-notes-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <RadioGroup<FormData>
        control={form.control}
        error={form.formState.errors.reason}
        name="reason"
        options={cancelReasonOptions}
      />
      <Input<FormData>
        control={form.control}
        error={form.formState.errors.note}
        label={intl.formatMessage({ id: 'forms.consultCancel.noteLabel' })}
        name="note"
      />
      <FormButtons hookForm={form} onCancel={onCancel} />
    </Form>
  );
};

export default ConsultCancelForm;
